import React from 'react';
import { Box, Typography, Button, Paper, Container, Link, Alert } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { loginWithTwitter, loginWithTelegram, loginWithDiscord } from '../store/slices/authSlice';
import { setPostAuthRedirect } from '../utils/postAuthRedirect';
import { RichText, useI18n } from '../i18n';
import { BRAND } from '../brand/config';
import { usePublicConfig } from '../hooks/usePublicConfig';

export default function Login() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const next = searchParams.get('next');
  const { t } = useI18n();
  const { config, loading } = usePublicConfig();
  const { login } = config;

  const beforeOAuth = () => {
    setPostAuthRedirect(next || '/');
  };

  const anyLogin = login.twitter || login.telegram || login.discord;

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 3, md: 6 } }}>
      <Paper elevation={0} variant="outlined" sx={{ p: { xs: 3, md: 4 }, textAlign: 'center', borderRadius: 3 }}>
        <Typography variant="h5" component="h1" gutterBottom fontWeight={700}>
          {t('login.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <RichText text={t('login.lead')} />
          {next ? (
            <Box component="span" display="block" sx={{ mt: 1 }}>
              {t('login.nextHint')}
            </Box>
          ) : null}
        </Typography>
        <Alert severity="info" variant="outlined" sx={{ mb: 3, textAlign: 'left' }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('login.onboardingTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('login.onboardingSteps')}
          </Typography>
        </Alert>
        {!loading && !config.escrow.configured ? (
          <Alert severity="warning" variant="outlined" sx={{ mb: 2, textAlign: 'left' }}>
            {t('login.escrowBetaHint')}{' '}
            <Link component={RouterLink} to="/status" underline="hover">
              {t('login.statusLink')}
            </Link>
          </Alert>
        ) : null}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {login.twitter ? (
            <Button
              variant="contained"
              size="large"
              onClick={() => {
                beforeOAuth();
                dispatch(loginWithTwitter());
              }}
            >
              {t('login.twitter')}
            </Button>
          ) : null}
          {login.telegram ? (
            <Button
              variant="outlined"
              size="large"
              onClick={() => {
                beforeOAuth();
                dispatch(loginWithTelegram());
              }}
            >
              {t('login.telegram')}
            </Button>
          ) : null}
          {login.discord ? (
            <Button
              variant="outlined"
              size="large"
              color="secondary"
              onClick={() => {
                beforeOAuth();
                dispatch(loginWithDiscord());
              }}
            >
              {t('login.discord')}
            </Button>
          ) : null}
          {!loading && !anyLogin ? (
            <Alert severity="error">{t('login.noProviders')}</Alert>
          ) : null}
        </Box>
        {config.discord?.botConfigured ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            {t('login.discordBotHint')}
          </Typography>
        ) : null}
        <Typography variant="body2" sx={{ mt: 3 }}>
          <Link component={RouterLink} to="/guide" underline="hover">
            {t('login.guideLink')}
          </Link>
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
          {BRAND.productName}
        </Typography>
      </Paper>
    </Container>
  );
}
